import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import { flushSync } from 'react-dom';

import {
  Instrument,
  ModelKind,
  Token,
  reconcileInstrument,
  validateForCalculate,
} from '../pages/exotics-pricer/contracts';

import { WebSocketContext } from './websocket-context/websocket-provider';

import { buildEngineInstrument, parseEngineResponse, asRecord } from './exotics-engine/engine-wire';
import type { PriceResult, LadderPayload } from './exotics-engine/engine-wire';

export type {
  Instrument,
  ModelKind,
  Token,
  PayType,
  ContractKind,
} from '../pages/exotics-pricer/contracts';

// Engine-wire types + helpers live in providers/exotics-engine/engine-wire.ts
// (extracted IM-1594, shared with the Market Probabilities page). Re-exported here so
// existing importers of these symbols from this provider keep working.
export type {
  SmilePoint,
  SabrParams,
  LadderPayload,
  PriceResult,
} from './exotics-engine/engine-wire';

// ---------- State + actions ----------

interface PricingState {
  instruments: Instrument[];
  results: Record<string, PriceResult>;
  errors: Record<string, string>;
  dirty: Record<string, boolean>;
  calculatedAt: Record<string, number>;
  loading: Record<string, boolean>;
  ladderCache: Record<string, LadderPayload>;
  ladderLoading: Record<string, boolean>;
  ladderFor: string | null;
  pendingRequest: Record<string, string>; // instrument_id -> latest request_id
}

type Action =
  | { type: 'ADD_INSTRUMENT'; instrument: Instrument }
  | { type: 'REMOVE_INSTRUMENT'; id: string }
  | { type: 'DUPLICATE_INSTRUMENT'; sourceId: string; newId: string }
  | { type: 'PATCH_INSTRUMENT'; id: string; patch: Partial<Instrument> }
  | { type: 'SET_INSTRUMENTS'; instruments: Instrument[] }
  | { type: 'CALCULATE_START'; id: string; requestId: string; withLadder: boolean }
  | { type: 'CALCULATE_RESULT'; id: string; requestId: string; result: PriceResult }
  | { type: 'LADDER_RESULT'; id: string; requestId: string; ladder: LadderPayload }
  | { type: 'CALCULATE_ERROR'; id: string; requestId: string; error: string }
  | { type: 'OPEN_LADDER'; id: string }
  | { type: 'CLOSE_LADDER' };

const newId = () => `i_${Math.random().toString(36).slice(2, 10)}`;
const newRequestId = () =>
  `pricing_exotics_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;

function reducer(state: PricingState, action: Action): PricingState {
  switch (action.type) {
    case 'ADD_INSTRUMENT':
      return {
        ...state,
        instruments: [...state.instruments, action.instrument],
        dirty: { ...state.dirty, [action.instrument.id]: true },
      };
    case 'REMOVE_INSTRUMENT': {
      const id = action.id;
      const drop = <T,>(rec: Record<string, T>) => {
        const out = { ...rec };
        delete out[id];
        return out;
      };
      return {
        ...state,
        instruments: state.instruments.filter((i) => i.id !== id),
        results: drop(state.results),
        errors: drop(state.errors),
        dirty: drop(state.dirty),
        calculatedAt: drop(state.calculatedAt),
        loading: drop(state.loading),
        ladderCache: drop(state.ladderCache),
        ladderLoading: drop(state.ladderLoading),
        pendingRequest: drop(state.pendingRequest),
        ladderFor: state.ladderFor === id ? null : state.ladderFor,
      };
    }
    case 'DUPLICATE_INSTRUMENT': {
      const src = state.instruments.find((i) => i.id === action.sourceId);
      if (!src) return state;
      const copy: Instrument = { ...src, id: action.newId };
      return {
        ...state,
        instruments: [...state.instruments, copy],
        dirty: { ...state.dirty, [copy.id]: true },
      };
    }
    case 'PATCH_INSTRUMENT': {
      const next = state.instruments.map((i) =>
        i.id === action.id ? reconcileInstrument(i, action.patch) : i,
      );
      return {
        ...state,
        instruments: next,
        dirty: { ...state.dirty, [action.id]: true },
      };
    }
    case 'SET_INSTRUMENTS':
      // Wholesale replace (IM-1955 handoff): swap the instrument list and drop
      // every per-id map, since the incoming instruments carry fresh ids. Marks
      // each new instrument dirty so it prices on the next Calculate.
      return {
        ...initialState,
        instruments: action.instruments,
        dirty: Object.fromEntries(action.instruments.map((i) => [i.id, true])),
      };
    case 'CALCULATE_START':
      return {
        ...state,
        loading: { ...state.loading, [action.id]: true },
        ladderLoading: action.withLadder
          ? { ...state.ladderLoading, [action.id]: true }
          : state.ladderLoading,
        errors: { ...state.errors, [action.id]: '' },
        pendingRequest: { ...state.pendingRequest, [action.id]: action.requestId },
      };
    case 'CALCULATE_RESULT': {
      // Latest-wins: drop if a newer request was issued for this instrument.
      // This action only carries the price/greeks side of the calculation —
      // the ladder lands separately via LADDER_RESULT (MVP pattern: two
      // parallel calls, the fast one paints the column immediately).
      if (state.pendingRequest[action.id] !== action.requestId) return state;
      return {
        ...state,
        results: { ...state.results, [action.id]: action.result },
        loading: { ...state.loading, [action.id]: false },
        dirty: { ...state.dirty, [action.id]: false },
        calculatedAt: { ...state.calculatedAt, [action.id]: Date.now() },
        errors: { ...state.errors, [action.id]: '' },
      };
    }
    case 'LADDER_RESULT': {
      // Latest-wins against the same pendingRequest cycle.
      if (state.pendingRequest[action.id] !== action.requestId) return state;
      return {
        ...state,
        ladderCache: { ...state.ladderCache, [action.id]: action.ladder },
        ladderLoading: { ...state.ladderLoading, [action.id]: false },
      };
    }
    case 'CALCULATE_ERROR':
      // Client-validation errors bypass the pendingRequest check.
      if (
        action.requestId !== 'client-validation' &&
        state.pendingRequest[action.id] !== action.requestId
      )
        return state;
      return {
        ...state,
        loading: { ...state.loading, [action.id]: false },
        ladderLoading: { ...state.ladderLoading, [action.id]: false },
        errors: { ...state.errors, [action.id]: action.error },
      };
    case 'OPEN_LADDER':
      return { ...state, ladderFor: action.id };
    case 'CLOSE_LADDER':
      return { ...state, ladderFor: null };
    default:
      return state;
  }
}

const initialState: PricingState = {
  instruments: [],
  results: {},
  errors: {},
  dirty: {},
  calculatedAt: {},
  loading: {},
  ladderCache: {},
  ladderLoading: {},
  ladderFor: null,
  pendingRequest: {},
};

// ---------- Context ----------

export interface PricingContextType {
  // state
  instruments: Instrument[];
  results: Record<string, PriceResult>;
  errors: Record<string, string>;
  dirty: Record<string, boolean>;
  calculatedAt: Record<string, number>;
  loading: Record<string, boolean>;
  ladderCache: Record<string, LadderPayload>;
  ladderLoading: Record<string, boolean>;
  ladderFor: string | null;
  // Locked token (from a tab-level token-pair selection). When set,
  // every new instrument inherits this token and the per-column token
  // selector is hidden by InstrumentCell.
  lockedToken: Token | null;
  // actions
  addInstrument: (seed?: Partial<Instrument>) => string;
  removeInstrument: (id: string) => void;
  duplicateInstrument: (id: string) => string;
  patchInstrument: (id: string, patch: Partial<Instrument>) => void;
  // Wholesale replace the instrument list from seeds (IM-1955 handoff). Each
  // seed is merged over the default one-touch seed, reconciled, and assigned a
  // fresh id.
  setInstruments: (seeds: Partial<Instrument>[]) => void;
  calculate: (id: string, opts?: { withLadder?: boolean }) => void;
  calculateAll: () => void;
  openLadder: (id: string) => void;
  closeLadder: () => void;
}

export const PricingContext = createContext<PricingContextType>({} as PricingContextType);

interface ProviderProps {
  children: ReactNode;
  // Optional token lock. When set, addInstrument forces this token and
  // InstrumentCell hides the per-column token selector. Used by the
  // single-token tab kind in the exotics pricer page.
  lockedToken?: Token;
}

// Default seed for a new instrument. Cast to Instrument because the
// discriminated union's per-branch required fields (e.g. `upper` for
// OneTouchUpper) are filled in by the user before calculate is enabled —
// validateForCalculate gates the WS send.
const DEFAULT_INSTRUMENT_SEED = {
  token: 'BTC' as const,
  contract: 'OneTouchUpper' as const,
  expiry: '',
  // 100,000 USDC matches the MVP default for OneTouch / DoubleNoTouch.
  // Notional `1` was only appropriate for Barrier (underlying-denominated);
  // the default contract here is OneTouch so quote-denominated 100k is the
  // right starting point.
  notional: 100000,
  notionalToken: 'quote' as const,
  upper: 0,
  payType: 'at_expiry' as const,
  // bs, not hybrid_lv_sv — vanilla/American options aren't priceable under
  // hybrid_lv_sv or sv on the backend today, only bs and lv. Defaulting to
  // hybrid_lv_sv meant switching contract to European/American silently
  // returned an empty pricer response until the user also changed the model.
  model: 'bs' as ModelKind,
};

export const PricingProvider = ({ children, lockedToken }: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { subscribeToChannel, unsubscribeFromChannel, sendMessage, isConnected } =
    useContext(WebSocketContext);

  // Keep a ref to the latest dispatch so WS callback closures can dispatch
  // without re-binding on every render.
  const dispatchRef = useRef(dispatch);
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  const addInstrument = useCallback(
    (seed?: Partial<Instrument>) => {
      const id = newId();
      // When a tab-level token lock is in force, every new instrument inherits
      // it. Caller-provided seed.token (if any) is overridden.
      const tokenOverride = lockedToken ? { token: lockedToken } : {};
      const merged = {
        ...DEFAULT_INSTRUMENT_SEED,
        ...(seed ?? {}),
        ...tokenOverride,
        id,
      } as Instrument;
      dispatch({
        type: 'ADD_INSTRUMENT',
        instrument: reconcileInstrument(merged, {}),
      });
      return id;
    },
    [lockedToken],
  );

  const removeInstrument = useCallback(
    (id: string) => dispatch({ type: 'REMOVE_INSTRUMENT', id }),
    [],
  );

  const duplicateInstrument = useCallback((id: string) => {
    const next = newId();
    dispatch({ type: 'DUPLICATE_INSTRUMENT', sourceId: id, newId: next });
    return next;
  }, []);

  const patchInstrument = useCallback(
    (id: string, patch: Partial<Instrument>) => dispatch({ type: 'PATCH_INSTRUMENT', id, patch }),
    [],
  );

  const setInstruments = useCallback(
    (seeds: Partial<Instrument>[]) => {
      const tokenOverride = lockedToken ? { token: lockedToken } : {};
      const built = seeds.map((seed) => {
        const merged = {
          ...DEFAULT_INSTRUMENT_SEED,
          ...seed,
          ...tokenOverride,
          id: newId(),
        } as Instrument;
        return reconcileInstrument(merged, {});
      });
      dispatch({ type: 'SET_INSTRUMENTS', instruments: built });
    },
    [lockedToken],
  );

  const calculateOne = useCallback(
    (inst: Instrument, withLadder: boolean) => {
      if (!isConnected || !sendMessage || !subscribeToChannel) return;
      const reason = validateForCalculate(inst);
      if (reason) {
        dispatchRef.current({
          type: 'CALCULATE_ERROR',
          id: inst.id,
          requestId: 'client-validation',
          error: reason,
        });
        return;
      }

      // One "cycle id" tracks this Calculate click for latest-wins. Both the
      // foreground (price) and background (ladder) WS messages dispatch
      // results against this cycle id, so a subsequent click invalidates
      // both in-flight responses cleanly.
      const cycleId = newRequestId();
      const priceMsgId = `${cycleId}__price`;
      const ladderMsgId = `${cycleId}__ladder`;
      dispatchRef.current({ type: 'CALCULATE_START', id: inst.id, requestId: cycleId, withLadder });

      const safeUnsubscribe = (channel: string) => {
        if (!unsubscribeFromChannel) return;
        try {
          unsubscribeFromChannel(channel);
        } catch {
          // some channels can't be unsubscribed server-side; local map is
          // still cleaned up.
        }
      };

      const errMsg = (e: unknown): string => {
        if (typeof e === 'string') return e;
        const msg = asRecord(e)?.message;
        return typeof msg === 'string' ? msg : JSON.stringify(e);
      };

      // Foreground: price + greeks (risk_ladder: false). Renders the column
      // as soon as it lands — the same MVP behaviour where each column
      // lights up independently the moment its fast call returns.
      const onPriceResponse = (raw: unknown) => {
        safeUnsubscribe(priceMsgId);
        const response = asRecord(raw);
        if (!response) {
          dispatchRef.current({
            type: 'CALCULATE_ERROR',
            id: inst.id,
            requestId: cycleId,
            error: 'no response from pricer',
          });
          return;
        }
        if (response.error) {
          dispatchRef.current({
            type: 'CALCULATE_ERROR',
            id: inst.id,
            requestId: cycleId,
            error: errMsg(response.error),
          });
          return;
        }
        const result = parseEngineResponse(response.result ?? response);
        if (!result) {
          dispatchRef.current({
            type: 'CALCULATE_ERROR',
            id: inst.id,
            requestId: cycleId,
            error: 'unexpected response shape from pricer',
          });
          return;
        }
        // flushSync forces React to commit this response before the next WS
        // frame's dispatch can batch with it. Without this, Calculate All
        // makes every column flip in a single render — the MVP gets natural
        // per-response paints because each axios .then lands on its own
        // microtask spaced by HTTP overhead. WS frames don't have that gap.
        flushSync(() => {
          dispatchRef.current({
            type: 'CALCULATE_RESULT',
            id: inst.id,
            requestId: cycleId,
            result,
          });
        });
      };

      // Background: risk ladder. Swallowed on failure — the column already
      // rendered from the price call; the modal can re-fetch on open.
      const onLadderResponse = (raw: unknown) => {
        safeUnsubscribe(ladderMsgId);
        const response = asRecord(raw);
        if (!response || response.error) return;
        const result = parseEngineResponse(response.result ?? response);
        if (!result?.ladder) return;
        // Capture the narrowed value: TS drops property narrowing across the
        // flushSync closure boundary, so read result.ladder into a local.
        const ladder = result.ladder;
        flushSync(() => {
          dispatchRef.current({
            type: 'LADDER_RESULT',
            id: inst.id,
            requestId: cycleId,
            ladder,
          });
        });
      };

      const baseParams = (ladder: boolean): Record<string, unknown> => ({
        request: ['PV', 'Delta', 'Theta', 'Gamma', 'Vega'],
        position: {
          instrument: buildEngineInstrument(inst),
          notional: inst.notional,
        },
        pricing_parameters: {
          model: inst.model,
          ...(inst.model === 'hybrid_lv_sv' && typeof inst.mixing === 'number'
            ? { mixing_weight: inst.mixing }
            : {}),
        },
        risk_ladder: ladder,
      });

      subscribeToChannel(priceMsgId, onPriceResponse);
      sendMessage(
        JSON.stringify({
          jsonrpc: '2.0',
          id: priceMsgId,
          method: 'generic_pricer',
          params: baseParams(false),
        }),
      );

      if (withLadder) {
        subscribeToChannel(ladderMsgId, onLadderResponse);
        sendMessage(
          JSON.stringify({
            jsonrpc: '2.0',
            id: ladderMsgId,
            method: 'generic_pricer',
            params: baseParams(true),
          }),
        );
      }
    },
    [isConnected, sendMessage, subscribeToChannel, unsubscribeFromChannel],
  );

  const calculate = useCallback(
    (id: string, opts?: { withLadder?: boolean }) => {
      const inst = state.instruments.find((i) => i.id === id);
      if (!inst) return;
      calculateOne(inst, opts?.withLadder ?? false);
    },
    [state.instruments, calculateOne],
  );

  const calculateAll = useCallback(() => {
    // Mirror the per-column Calculate button: fire price + ladder for every
    // instrument. A small stagger between sends gives the engine a head
    // start on each column — combined with flushSync on response, that
    // produces the MVP's "lights-up-one-at-a-time" behaviour instead of a
    // single coalesced render once every response is in.
    state.instruments.forEach((inst, idx) => {
      setTimeout(() => calculateOne(inst, true), idx * 40);
    });
  }, [state.instruments, calculateOne]);

  const openLadder = useCallback((id: string) => dispatch({ type: 'OPEN_LADDER', id }), []);
  const closeLadder = useCallback(() => dispatch({ type: 'CLOSE_LADDER' }), []);

  const value = useMemo<PricingContextType>(
    () => ({
      instruments: state.instruments,
      results: state.results,
      errors: state.errors,
      dirty: state.dirty,
      calculatedAt: state.calculatedAt,
      loading: state.loading,
      ladderCache: state.ladderCache,
      ladderLoading: state.ladderLoading,
      ladderFor: state.ladderFor,
      lockedToken: lockedToken ?? null,
      addInstrument,
      removeInstrument,
      duplicateInstrument,
      patchInstrument,
      setInstruments,
      calculate,
      calculateAll,
      openLadder,
      closeLadder,
    }),
    [
      state,
      lockedToken,
      addInstrument,
      removeInstrument,
      duplicateInstrument,
      patchInstrument,
      setInstruments,
      calculate,
      calculateAll,
      openLadder,
      closeLadder,
    ],
  );

  return <PricingContext.Provider value={value}>{children}</PricingContext.Provider>;
};

// Test-only surface: the pure reducer, its initial state, and the default seed,
// so the SET_INSTRUMENTS replace behaviour and the handoff seeding can be
// exercised without the WS/React stack.
export const __pricingTestInternals = { reducer, initialState, DEFAULT_INSTRUMENT_SEED };
